import { connect } from '@reverbdotcom/commons/src/connect';
import * as elog from '@reverbdotcom/commons/src/elog';
import { ExecutionResult } from 'graphql';
import I18n from 'i18n-js';
import React from 'react';

import { RCFavoriteButton, RCFavoriteButtonProps, RCIcon } from '@reverbdotcom/cadence/components';
import { HeartStraightIcon, HeartStraightFillIcon } from '@reverbdotcom/cadence/icons/react';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { buildClickedRemoveFavoriteSearch } from '@reverbdotcom/commons/src/event_tracking/favorites';
import { EntityType, EntityTypeName, mapFavoriteToEntityType } from '@reverbdotcom/commons/src/favorites/entity_type';
import { Core_FindFavoriteQuery, DeleteMyFavoriteMutation } from '@reverbdotcom/commons/src/gql/graphql';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import classNames from 'classnames';
import { DeleteMyFavoriteMutationFn, useDeleteMyFavorite, withDeleteMyFavoriteMutation } from './delete_my_favorite_mutation';
import { useFindFavorite } from './find_favorite_context';
import { redirectToSignin } from '@reverbdotcom/commons/src/signup_signin_helper';
import LocationUtil from '../shared/location_util';
import { FOLLOW_PROMPT_PARAM } from '../shared/constants';

type Core_FindFavoriteQuery_Favorite = Core_FindFavoriteQuery['findFavorite']['favorite'];

export interface ExternalProps {
  handleClickOverride?: () => void;
  renderWithBanner: boolean;

  canFavorite?: boolean; // used on non-marketplace pages where findFavoriteContext can't discern favorite status
  isFavorited?: boolean; // used on non-marketplace pages where findFavoriteContext can't discern favorite status
  entityType?: EntityTypeName; // used on non-marketplace pages where findFavoriteContext can't discern favorite status
  condensed?: boolean;
  favoriteId?: string;
  handleFavoriteRemove?: (result: ExecutionResult<DeleteMyFavoriteMutation>) => void;
  showCallout?: boolean;
  buttonTypeOverride?: 'link' | 'secondary' | 'muted';
  componentName?: string;
  iconButtonSize?: RCFavoriteButtonProps['size'];
  iconButtonVariant?: RCFavoriteButtonProps['variant'];
}

interface MutationProps {
  deleteMyFavorite: DeleteMyFavoriteMutationFn;
}

export type Props = ExternalProps & MutationProps;

export function InternalFavoriteButton(props: Props) {
  const url = useURL();
  const user = useUser();
  const {
    favorite,
    setFavorite,
    setIsToastOpen,
    canFavorite,
    autoSaveFavorite,
  } = useFindFavorite();
  const id = props.favoriteId || favorite.id;
  const { deleteMyFavorite } = useDeleteMyFavorite(props.deleteMyFavorite, id);
  const entityType = props.entityType || mapFavoriteToEntityType(favorite);
  const disabled = !(props.canFavorite || canFavorite);
  const favorited = props.isFavorited || favorite.favorited;

  const handleSaveFavorite = async function () {
    await autoSaveFavorite(props.componentName);
  };

  const handleClick = async function (e) {
    e.preventDefault();

    if (disabled) {
      return;
    }

    if (favorited) {
      try {
        const result = await deleteMyFavorite();
        if (props.handleFavoriteRemove) { props.handleFavoriteRemove(result); }
        const newFavorite: Core_FindFavoriteQuery_Favorite = {
          ...favorite,
          favorited: false,
        };
        setFavorite(newFavorite);
        trackEvent(buildClickedRemoveFavoriteSearch(newFavorite, url));
        setIsToastOpen(true);
      } catch (e) {
        elog.error(
          e.message,
          { componentName: 'FavoriteButton' },
          e,
        );
      }
    } else {
      if (props.handleClickOverride) {
        props.handleClickOverride();
      } else if (user.loggedOut) {
        redirectToSignin('FavoriteButton', LocationUtil.currentPageWithParams({ [FOLLOW_PROMPT_PARAM]: true }));
      } else {
        await handleSaveFavorite();
      }
    }
  };

  const buttonType = props.buttonTypeOverride || (entityType === EntityType.Product || props.renderWithBanner ? 'secondary' : 'link');
  const classes = classNames(
    'favorite-button__button',
    {
      'favorite-button__button--link': buttonType === 'link',
      'favorite-button__button--secondary': buttonType === 'secondary',
      'favorite-button__button--muted': buttonType === 'muted',
      'favorite-button__button--disabled': disabled,
    },
  );

  return (
    props.condensed ?
      <RCFavoriteButton
        favorited={favorited}
        onClick={handleClick}
        disabled={disabled}
        size={props.iconButtonSize}
        variant={props.iconButtonVariant}
      />
      :
      <button
        className={classes}
        onClick={handleClick}
        type="button"
        data-disabled={disabled || null}
      >
        <RCIcon
          icon={favorited ? <HeartStraightFillIcon /> : <HeartStraightIcon />}
          size="button"
          spacing={buttonType === 'link' ? 'right' : undefined}
          color={buttonType !== 'link' && favorited ? 'accent-loud' : undefined}
        />
        {
          favorited ?
            I18n.t('discovery.favorites.favoriteButton.saved') :
            <>
              <span className="favorite-button__save-text--full">
                {I18n.t(`discovery.favorites.favoriteButton.save.${entityType}`)}
              </span>
              <span className="favorite-button__save-text--compact">
                {I18n.t('discovery.favorites.favoriteButton.save.compact')}
              </span>
            </>
        }
      </button>
  );
}

export const FavoriteButton = connect<ExternalProps>([
  withDeleteMyFavoriteMutation,
])(InternalFavoriteButton);
